import * as React from 'react';
import { useCallback, useState, useEffect, useMemo } from 'react';
import iconFilterDisabled from '../../../assets/images/icon-filter-disabled.svg';
import iconFilter from '../../../assets/images/icon-filter.svg';
import arrowDown from '../../../assets/images/icons/black.svg';
import { AccessLevel, AsyncStatus } from '../../../common/enums';
import { AutomationIds, AutomationTypes } from '../../../common/enums/AutomationElements.enum';
import { Column, IFilter } from '../../../common/interfaces';
import { Button, ButtonGroup } from '../../../components/Buttons';
import { useLanguageElements } from '../../../language-elements/withLanguageElementsHOC';
import viewClient from '../../../http-clients/ViewClient';
import FilterMenu from './FilterMenu/FilterMenu';
import FilterWrapper from './FilterWrapper';
import { isReportLoading, transformFiltersToFiltersUI, transformApiColumnsToColumns } from './FilterUtil';
import { getFilterForEditing } from './FilterWrapperUtils';
import { useSelector } from 'react-redux';
import { reportColumnsSelector, reportDataSelector } from '../Report/Selectors';
import { FilterActionType, FilterUI } from './types';

export interface Props {
    currentUserAccessLevel: AccessLevel;
    viewId: string;
}

const FilterContainer: React.FC<Props> = (props) => {
    const { currentUserAccessLevel, viewId } = props;
    const languageElements = useLanguageElements();
    const reportData = useSelector(reportDataSelector);
    const reportColumns = useSelector(reportColumnsSelector);

    const fields: Column[] = useMemo(() => {
        if (reportColumns.status === AsyncStatus.DONE) {
            return transformApiColumnsToColumns(reportColumns.data as any[]);
        }
        return [];
    }, [reportColumns]);

    const [isLoadingData, setIsLoadingData] = useState<boolean>(isReportLoading(reportData.status));

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    const [activeFilterIndex, setActiveFilterIndex] = useState(-1);
    const [lastActiveFilterIndex, setLastActiveFilterIndex] = useState(-1);
    const [editFilterIndex, setEditFilterIndex] = useState(-1);
    const [filterAction, setFilterAction] = useState<FilterActionType>(FilterActionType.NEW_FILTER);

    const [filtersUI, setFiltersUI] = useState<FilterUI[]>([]);
    const [isLoadingFilters, setIsLoadingFilters] = useState(false);

    // Fetch filters using the new endpoint with isumkd=true to get unmasked column IDs
    const fetchFilters = useCallback(async () => {
        try {
            setIsLoadingFilters(true);
            const response = await viewClient.getFilters(viewId, true); // Use isumkd=true
            const filters: IFilter[] = response.data;
            setFiltersUI(transformFiltersToFiltersUI(filters, fields));
        } catch (error) {
            setFiltersUI([]);
        } finally {
            setIsLoadingFilters(false);
        }
    }, [viewId]);

    useEffect(() => {
        fetchFilters();
    }, [fetchFilters]);

    useEffect(() => {
        setIsLoadingData(isReportLoading(reportData.status));
    }, [reportData.status]);

    const handleFilterModalClose = () => {
        setIsFilterModalOpen(false);
    };
    const handleFilterButtonClick = () => {
        if (isLoadingData) {
            return;
        }
        if (!filtersUI?.length) {
            openNewFilterModal();
            return;
        }
        toggleFilterState();
    };

    const openNewFilterModal = () => {
        setIsFilterModalOpen(true);
        setFilterAction(FilterActionType.NEW_FILTER);
    };

    const toggleFilterState = () => {
        if (activeFilterIndex === -1) {
            enableFilter();
        } else {
            handleFilterOff();
        }
    };

    const enableFilter = () => {
        const indexToUse = lastActiveFilterIndex !== -1 ? lastActiveFilterIndex : 0;
        if (filtersUI[indexToUse]) {
            setActiveFilterIndex(indexToUse);
            handleApplyExistingFilter(filtersUI[indexToUse]);
        }
    };

    const handleCancelFilter = (): void => {
        setIsFilterModalOpen(false);
    };

    const handleNewFilter = (): void => {
        setIsFilterModalOpen(true);
        setIsMenuOpen(false);
        setFilterAction(FilterActionType.NEW_FILTER);
        setEditFilterIndex(-1);
    };

    const handleFilterOff = (): void => {
        if (activeFilterIndex !== -1) {
            setLastActiveFilterIndex(activeFilterIndex);
        }
        setActiveFilterIndex(-1);
        setIsMenuOpen(false);
    };

    const handleFilterSelect = (filter: FilterUI): void => {
        const index = filtersUI?.findIndex((f) => f.id === filter.id) ?? -1;
        if (index !== -1) {
            setActiveFilterIndex(index);
            setIsMenuOpen(false);
            handleApplyExistingFilter(filter);
        }
    };

    const handleApplyExistingFilter = (filter: FilterUI): void => {
        // This function applies an existing filter without creating/updating it
        // Just apply the filter locally - no server call needed since filter already exists
        setIsLoadingData(true);
        try {
            setIsLoadingData(false);
        } catch (error) {
            setIsLoadingData(false);
        }
    };

    const handleEditFilter = (filter: FilterUI): void => {
        const index = filtersUI?.findIndex((f) => f.id === filter.id) ?? -1;
        if (index !== -1) {
            setEditFilterIndex(index);
            setFilterAction(FilterActionType.EDIT_FILTER);
            setIsFilterModalOpen(true);
            setIsMenuOpen(false);
        }
    };

    const handleDuplicateFilter = (filter: FilterUI): void => {
        const index = filtersUI?.findIndex((f) => f.id === filter.id) ?? -1;
        if (index !== -1) {
            setEditFilterIndex(index);
            setFilterAction(FilterActionType.DUPLICATE_FILTER);
            setIsFilterModalOpen(true);
            setIsMenuOpen(false);
        }
    };

    const activateNewlyCreatedFilter = (filter: FilterUI) => {
        if (filtersUI.length === 0) {
            return;
        }

        const newFilterIndex =
            filterAction === FilterActionType.DUPLICATE_FILTER
                ? filtersUI.findIndex((f) => f.id === filter.id || f.name === filter.name)
                : filtersUI.length - 1;

        if (newFilterIndex !== -1) {
            setActiveFilterIndex(newFilterIndex);
        }
    };

    const handleApplyFilter = async (filter: FilterUI): Promise<boolean> => {
        // This function only handles creating/updating filters from FilterWrapper
        // It should NOT be called when selecting existing filters from the menu
        try {
            if (filterAction === FilterActionType.NEW_FILTER || filterAction === FilterActionType.DUPLICATE_FILTER) {
                await viewClient.addFilter(viewId, filter, true);
            } else if (filterAction === FilterActionType.EDIT_FILTER) {
                await viewClient.updateFilter(viewId, filter, true);
            }

            setIsFilterModalOpen(false);
            await fetchFilters();

            // For new filters and duplicates, set the newly created filter as active
            if (filterAction === FilterActionType.NEW_FILTER || filterAction === FilterActionType.DUPLICATE_FILTER) {
                activateNewlyCreatedFilter(filter);
            }

            setIsLoadingData(false);
            return true;
        } catch (error) {
            setIsLoadingData(false);
            return false;
        }
    };

    const handleDeleteFilter = async (filterId?: string): Promise<boolean> => {
        try {
            if (!filterId) {
                return false;
            }
            setIsLoadingData(true);
            await viewClient.deleteFilter(viewId, filterId);

            const deletedFilterIndex = filtersUI.findIndex((filter) => filter.id === filterId);
            setIsMenuOpen(false);

            if (activeFilterIndex !== -1) {
                if (filtersUI[activeFilterIndex]?.id === filterId) {
                    setActiveFilterIndex(-1);
                    setIsFilterModalOpen(false);
                } else if (deletedFilterIndex !== -1 && activeFilterIndex > deletedFilterIndex) {
                    setActiveFilterIndex(activeFilterIndex - 1);
                }
            }
            await fetchFilters();

            if (filtersUI.length <= 1) {
                setIsFilterModalOpen(false);
                setEditFilterIndex(-1);
                setFilterAction(FilterActionType.NEW_FILTER);
            }

            setIsLoadingData(false);
            return true;
        } catch (error) {
            setIsLoadingData(false);
            return false;
        }
    };

    const handleFilterMenuClick = () => {
        if (isLoadingData || isLoadingFilters) {
            return;
        }
        setIsMenuOpen(!isMenuOpen);
    };

    const getFilterButtonName = useCallback((): string => {
        if (activeFilterIndex === -1) {
            return languageElements.ADHOC_FILTER_OFF;
        }
        if (!filtersUI || activeFilterIndex >= filtersUI.length || activeFilterIndex < 0) {
            return languageElements.ADHOC_FILTER_OFF;
        }
        return filtersUI[activeFilterIndex]?.name || languageElements.ADHOC_FILTER_OFF;
    }, [activeFilterIndex, filtersUI, languageElements]);

    return (
        <div className={`filter-container${isLoadingData || isLoadingFilters ? ' disabled' : ''}`}>
            <FilterWrapper
                onDeleteFilter={handleDeleteFilter}
                onCancelFilter={handleCancelFilter}
                onApplyFilter={(filterToApply: FilterUI) => handleApplyFilter(filterToApply)}
                filter={getFilterForEditing(filterAction, filtersUI, editFilterIndex, fields, languageElements, currentUserAccessLevel)}
                fields={fields}
                picklistSymbolImageMap={new Map()}
                currentUserAccessLevel={currentUserAccessLevel}
                filterAction={filterAction}
                showModal={isFilterModalOpen}
                onModalClose={handleFilterModalClose}
            />
            {!filtersUI || !filtersUI.length ? (
                <div style={{ position: 'relative' }}>
                    <Button
                        className="view-filter-button"
                        dataClientId={AutomationIds.FILTER_MODAL_BUTTON}
                        dataClientType={AutomationTypes.BUTTON}
                        icon={iconFilter}
                        imageAltText={languageElements.FILTER_ALT_TEXT}
                        text={languageElements.VIEW_FILTER}
                        onClick={handleFilterButtonClick}
                        disabled={isLoadingData || isLoadingFilters}
                    />
                </div>
            ) : (
                <div>
                    <ButtonGroup
                        dataClientId={AutomationIds.FILTER_BUTTON_GROUP}
                        dataClientType={AutomationTypes.BUTTON_GROUP}
                        className={`view-header-button-group ${activeFilterIndex === -1 ? '' : 'selected'}`}
                    >
                        <Button
                            dataClientId={AutomationIds.FILTER_DISABLE_BUTTON}
                            dataClientType={AutomationTypes.BUTTON}
                            icon={activeFilterIndex === -1 ? iconFilterDisabled : iconFilter}
                            className="filter-button disable-filter-button"
                            onClick={handleFilterButtonClick}
                            disabled={isLoadingData || isLoadingFilters}
                        />
                        <Button
                            dataClientId={AutomationIds.FILTER_MODAL_BUTTON}
                            dataClientType={AutomationTypes.BUTTON}
                            className="filter-button"
                            text={getFilterButtonName()}
                            onClick={handleFilterMenuClick}
                            icon={arrowDown}
                            disabled={isLoadingData || isLoadingFilters}
                        />
                    </ButtonGroup>
                </div>
            )}
            {isMenuOpen && !isLoadingData && !isLoadingFilters && (
                <FilterMenu
                    filterList={filtersUI}
                    onNewFilter={handleNewFilter}
                    onFilterOff={handleFilterOff}
                    onFilterSelect={handleFilterSelect}
                    onFilterEdit={handleEditFilter}
                    onFilterDuplicate={handleDuplicateFilter}
                    onFilterDelete={handleDeleteFilter}
                    currentUserAccessLevel={currentUserAccessLevel}
                />
            )}
        </div>
    );
};

export default FilterContainer;
